import React from 'react';
import { Card, Skeleton, SkeletonItem, Text, tokens, mergeClasses } from '@fluentui/react-components';
import { useSummaryCardBarStyles } from '../styles/SummaryCardBar.styles';

// ── Accent colour presets ────────────────────────────────────────────────────
// Import these in pages to keep colour choices co-located with the component.
export const SUMMARY_CARD_COLORS = {
  brand:    tokens.colorBrandBackground,
  pending:  tokens.colorPaletteYellowBackground2,
  approved: tokens.colorStatusSuccessBackground2,
  denied:   tokens.colorStatusDangerBackground2,
  /**
   * Fluent DataViz — Slot 1 (Light theme primary colors).
   * Sourced from the Fluent 2 Figma design library; not exposed as a runtime
   * token by @fluentui/tokens, so it's declared here as the single source of truth.
   */
  dataVizSlot1: '#637CEF',
  /**
   * Fluent DataViz — Slot 2 (Light theme primary colors).
   * Sourced from the Fluent 2 Figma design library; not exposed as a runtime
   * token by @fluentui/tokens, so it's declared here as the single source of truth.
   */
  dataVizSlot2: '#E3008C',
  /**
   * Fluent DataViz — Slot 3 (Light theme primary colors).
   * Sourced from the Fluent 2 Figma design library; not exposed as a runtime
   * token by @fluentui/tokens, so it's declared here as the single source of truth.
   */
  dataVizSlot3: '#2AA0A4',
} as const;

// ── Public types ─────────────────────────────────────────────────────────────
export interface SummaryCardItem {
  label: string;
  value: number | string;
  /** A CSS colour value — use SUMMARY_CARD_COLORS or any valid colour string. */
  accentColor?: string;
  /** When provided, clicking this card calls onCardClick with this value. */
  filterValue?: string;
}

interface SummaryCardBarProps {
  cards: SummaryCardItem[];
  isLoading?: boolean;
  activeFilter?: string;
  onCardClick?: (filterValue: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
export const SummaryCardBar: React.FC<SummaryCardBarProps> = ({
  cards,
  isLoading = false,
  activeFilter,
  onCardClick,
}) => {
  const styles = useSummaryCardBarStyles();

  return (
    <div
      className={styles.row}
      style={{ '--summary-col-count': String(cards.length) } as React.CSSProperties}
    >
      {cards.map((card, i) => {
        const isClickable = !!card.filterValue && !!onCardClick && !isLoading;
        const isActive = isClickable && activeFilter === card.filterValue;

        return (
          <Card
            key={i}
            className={mergeClasses(styles.card, isClickable && styles.cardClickable, isActive && styles.cardActive)}
            style={card.accentColor ? { borderLeft: `4px solid ${card.accentColor}` } : undefined}
            onClick={isClickable ? () => onCardClick!(card.filterValue!) : undefined}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onCardClick!(card.filterValue!); } : undefined}
          >
            {isLoading ? (
              <>
                <Skeleton>
                  <SkeletonItem style={{ width: '100px', height: `${tokens.lineHeightBase300}` }} />
                </Skeleton>
                <Skeleton>
                  <SkeletonItem style={{ width: '60px', height: `${tokens.lineHeightBase600}` }} />
                </Skeleton>
              </>
            ) : (
              <>
                <Text className={styles.label}>{card.label}</Text>
                <div className={styles.value}>{card.value}</div>
              </>
            )}
          </Card>
        );
      })}
    </div>
  );
};
